import { useContext, useRef } from "react";
import { PickupLocationContext } from "../../store/PickupLocationContext";
import type { ClientPickupFormData, DayOption } from "../../utils/types";

type ChangePickupProps = {
  formData: ClientPickupFormData;
  onFormDataChange: (clientPickupFormData: ClientPickupFormData) => void;
  isPickupLocationLocked?: boolean;
};

const PickupEditForm = ({
  formData,
  onFormDataChange,
  isPickupLocationLocked = false,
}: ChangePickupProps) => {
  const { pickupLocations } = useContext(PickupLocationContext);
  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);
  const phoneRef = useRef(null);

  const {
    friendPickupChecked,
    friendFirstName,
    friendLastName,
    friendPhone,
    selectedPickupLocation,
    selectedDay,
  } = formData;

  const locationOptions =
    pickupLocations.length > 0 ? (
      pickupLocations.map((location) => {
        return (
          <option key={location._id} value={location.name}>
            {location.name}
          </option>
        );
      })
    ) : (
      <option>-- No pickup locations available at the moment --</option>
    );

  const dayOptions = selectedPickupLocation?.days.map((day: DayOption) => {
    return (
      <option key={day.day}>
        {day.day} {day.startTime} - {day.endTime}
      </option>
    );
  });

  function getPickupLocationFromName(pickupLocationName: string) {
    const pickupLocation = pickupLocations.find((location) => {
      return location.name === pickupLocationName;
    });

    return pickupLocation;
  }
  function onPickupLocationChange(value: string) {
    if (isPickupLocationLocked) return;

    const newLocation = getPickupLocationFromName(value);

    if (newLocation) {
      const foundDayOption = newLocation.days.find(
        (dayOption) => dayOption.day === selectedDay,
      );

      onFormDataChange({
        ...formData,
        selectedPickupLocation: getPickupLocationFromName(value),
        selectedDay: foundDayOption?.day
          ? selectedDay
          : newLocation.days[0].day,
      });
    }
  }
  return (
    <div className="pickup-edit-form">
      <div className="client-dashboard-form-input-with-label">
        <label htmlFor="pickup-dropdown">My pickup location</label>
        <select
          className={
            isPickupLocationLocked
              ? "dropdown pickup-location-locked"
              : "dropdown"
          }
          id="pickup-dropdown"
          value={selectedPickupLocation?.name}
          disabled={isPickupLocationLocked}
          onChange={(e) => {
            onPickupLocationChange(e.target.value);
          }}
        >
          {locationOptions}
        </select>
        {isPickupLocationLocked && (
          <p className="pickup-location-locked-hint">
            You signed up for pickup at Alnarp&apos;s Farm, so this location
            cannot be changed. You can still change your pickup day below.
          </p>
        )}
      </div>
      <div className="client-dashboard-form-input-with-label">
        <label htmlFor="pickup-day">Preferred day of pickup</label>
        <select
          className="dropdown capitalized"
          id="day-dropdown"
          value={selectedDay}
          onChange={(e) =>
            onFormDataChange({
              ...formData,
              selectedDay: e.target.value,
            })
          }
        >
          {dayOptions}
        </select>
      </div>

      <div className="client-dashboard-checkbox-row">
        <label className="friend-pickup-toggle">
          <input
            type="checkbox"
            checked={friendPickupChecked}
            onChange={(e) =>
              onFormDataChange({
                ...formData,
                friendPickupChecked: e.target.checked,
              })
            }
          />
          <span>Someone else will pick up my delivery.</span>
        </label>
      </div>
      <div className="client-dashboard-form-input-with-label">
        <label htmlFor="friend-name">First Name:</label>
        <input
          type="text"
          id="friend-first-name"
          ref={firstNameRef}
          name="friendPickupFirstName"
          value={!friendFirstName ? "" : friendFirstName}
          onChange={(e) =>
            onFormDataChange({
              ...formData,
              friendFirstName: e.target.value,
            })
          }
        />
      </div>
      <div className="client-dashboard-form-input-with-label">
        <label htmlFor="friend-name">Last Name:</label>
        <input
          type="text"
          id="friend-last-name"
          ref={lastNameRef}
          name="friendPickupFLastName"
          value={!friendLastName ? "" : friendLastName}
          onChange={(e) =>
            onFormDataChange({
              ...formData,
              friendLastName: e.target.value,
            })
          }
        />
      </div>
      <div className="client-dashboard-form-input-with-label">
        <label htmlFor="friend-phone">Phone: </label>
        <input
          id="friend-phone"
          ref={phoneRef}
          type="tel"
          value={!friendPhone ? "" : friendPhone}
          onChange={(e) =>
            onFormDataChange({
              ...formData,
              friendPhone: e.target.value,
            })
          }
        />
      </div>
    </div>
  );
};

export default PickupEditForm;
